import type { CatalogCollection } from "../api/types";
import { sizeDims, type DoorSize } from "../geometry/doorSize";
import type { BlendEntry, DoorObject, DoorSelection, VisualizerDocument } from "./visualizerStore";

interface HasDocRenderCatalog {
  document: VisualizerDocument;
  render: { compositeUrl: string | null; integratedUrl: string | null; blends: Record<string, BlendEntry> };
  catalog: { collection: CatalogCollection | null };
  ui: { selectedDoorId: string | null };
}

/** Doors in stable detection order. */
export function orderedDoors(s: { document: VisualizerDocument }): DoorObject[] {
  return s.document.doorOrder.map((id) => s.document.doors[id]).filter(Boolean);
}

export function selectedDoor(s: { document: VisualizerDocument; ui: { selectedDoorId: string | null } }): DoorObject | null {
  const id = s.ui.selectedDoorId;
  return id ? s.document.doors[id] ?? null : null;
}

/** A door is renderable once it has at least a design + a color. */
export function isConfigured(d: DoorObject): boolean {
  return !!(d.selection.designId && d.selection.colorId);
}

/** The size driving this door's catalogue/pricing/render: manual override wins, else the server's
 * detected verdict, else Double (pre-feature sessions whose detection carried no size). */
export function effectiveSize(d: DoorObject): DoorSize {
  return d.sizeOverride ?? d.detected.doorType ?? "double";
}

export function configuredDoors(s: { document: VisualizerDocument }): DoorObject[] {
  return orderedDoors(s).filter(isConfigured);
}

/** THE canonical render/blend input key for a door — every field that changes what the server draws.
 * `sel` is the whole selection object, so glassTypeId participates — switching tint invalidates.
 * `tp` makes profile edits invalidate exactly like a quad edit (the detected occlusionSvgPath is
 * frozen per door, so it can't go stale and stays out of the key). Shared by the composite prefix
 * cache AND the per-door blend map so "blended & fresh" can never diverge from what a re-render
 * would consider changed. */
export function doorInputHash(door: DoorObject, collectionId: string | null): string {
  const dims = sizeDims(effectiveSize(door));
  return JSON.stringify({ q: door.quad, sel: door.selection, c: collectionId, w: dims.width, h: dims.height, tp: door.topProfile ?? null });
}

/** A door is "blended & fresh" when its last AI blend succeeded for exactly the door's CURRENT
 * inputs: an entry exists, the server did NOT fall back to the geometric composite, and the stored
 * hash matches. Editing the door (quad/selection/size/profile/collection) makes this false with no
 * imperative invalidation — freshness is a read-time hash comparison, mirroring render.cache. */
export function isBlendedFresh(door: DoorObject, blends: Record<string, BlendEntry>, collectionId: string | null): boolean {
  const e = blends[door.id];
  return !!e && !e.usedFallback && e.inputHash === doorInputHash(door, collectionId);
}

export interface BlendScope {
  /** Scope doors still needing a blend: never blended, inputs changed, or the last attempt fell back. */
  staleDoors: DoorObject[];
  /** Scope doors whose last attempt AT THE CURRENT INPUTS degraded to the standard render — these
   * drive the "Door N used the standard render — try again" hint (a fallback whose inputs have since
   * changed is just an ordinary stale door; the hint would be about an obsolete configuration). */
  fallbackDoors: DoorObject[];
  /** "Photorealistic preview ready": every scope door is blended-fresh (and there is at least one). */
  ready: boolean;
}

/** Per-door blend state over the doors in scope (the selected door, or every CONFIGURED door in
 * ALL mode). Pure so the page's button/ready/hint logic is testable at the store level. */
export function blendScopeState(scopeDoors: DoorObject[], blends: Record<string, BlendEntry>, collectionId: string | null): BlendScope {
  const staleDoors = scopeDoors.filter((d) => !isBlendedFresh(d, blends, collectionId));
  const fallbackDoors = staleDoors.filter((d) => {
    const e = blends[d.id];
    return !!e && e.usedFallback && e.inputHash === doorInputHash(d, collectionId);
  });
  return { staleDoors, fallbackDoors, ready: scopeDoors.length > 0 && staleDoors.length === 0 };
}

/** Picker state for ALL-doors mode: field-wise agreement across the doors. A field reads as
 * selected only when EVERY door actually has that value; a mixed field reads as null (nothing
 * highlighted) until the next pick unifies it — the pickers never claim a value some door doesn't
 * really have. Pre-glass persisted doors lack glassTypeId, so fields are read defensively. */
export function sharedSelection(doors: DoorObject[]): DoorSelection {
  const first = doors[0]?.selection;
  const agree = <K extends keyof DoorSelection>(key: K): string | null => {
    const v = first?.[key] ?? null;
    return v !== null && doors.every((d) => (d.selection[key] ?? null) === v) ? v : null;
  };
  return {
    designId: agree("designId"),
    colorId: agree("colorId"),
    windowOptionId: agree("windowOptionId"),
    glassTypeId: agree("glassTypeId"),
    hardwareId: agree("hardwareId"),
    constructionId: agree("constructionId"),
  };
}

/** What the customer sees: AI blend if present, else the chained composite, else the original photo. */
export function displayUrl(s: HasDocRenderCatalog): string | null {
  return s.render.integratedUrl ?? s.render.compositeUrl ?? s.document.photoUrl;
}

/** Resolve the chosen option names for a door from the active collection. */
export function resolveDoorNames(collection: CatalogCollection | null, selection: DoorSelection): string[] {
  if (!collection) return [];
  const names: string[] = [];
  const design = collection.designs.find((d) => d.id === selection.designId);
  if (design) names.push(design.name);
  const color = collection.colors.find((c) => c.id === selection.colorId);
  if (color) names.push(color.name);
  const window = selection.windowOptionId ? collection.windows.find((w) => w.id === selection.windowOptionId) : undefined;
  if (window) names.push(window.name);
  const hardware = selection.hardwareId ? collection.hardware.find((h) => h.id === selection.hardwareId) : undefined;
  if (hardware) names.push(hardware.name);
  const construction = selection.constructionId ? collection.constructions.find((c) => c.id === selection.constructionId) : undefined;
  if (construction) names.push(construction.name);
  return names;
}

export interface LegendRow {
  id: string;
  index: number;
  configured: boolean;
  names: string[];
  size: DoorSize;
  // Blended-fresh (real AI blend for the door's current inputs) — surfaces per-door photoreal state
  // in the legend so a multi-door session shows at a glance which doors still need the upgrade.
  blended: boolean;
}

export function legendRows(s: HasDocRenderCatalog): LegendRow[] {
  const collection = s.catalog.collection;
  return orderedDoors(s).map((d) => ({
    id: d.id,
    index: d.index,
    configured: isConfigured(d),
    names: resolveDoorNames(collection, d.selection),
    size: effectiveSize(d),
    blended: isBlendedFresh(d, s.render.blends, s.document.collectionId),
  }));
}
